/**
 * Titelbild eines Gerichts hochladen, anzeigen und entfernen.
 *
 * Anders als Tagebuchfotos geht das Bild sofort auf den Server (Entscheidung
 * E9) — es gibt keine Warteschlange auf dem Gerät. Ohne Verbindung sagt die
 * Oberfläche das, statt ein Bild zu versprechen, das nie ankommt.
 *
 * Ende-zu-Ende (E21, 26.10.2026): Mit Kontoschlüssel geht das Bild als
 * `KWE1`-Datei hoch, das Gerät öffnet es selbst. Geteilte Kopien packt das
 * gebende Gerät mit dem Haushaltsschlüssel; das empfangende verschlüsselt sie
 * danach mit seinem eigenen (`resealRecipeImages`).
 */

import { looksSealed, PHOTO_CONTENT_TYPE, SEALED_FILE_CONTENT_TYPE } from '@kraftwerk/shared';
import { API_URL } from '../api.js';
import {
  knownKeys,
  openFile,
  readAccountKey,
  readEncryptionSetup,
  readHouseholdKey,
  sealFile,
} from '../crypto/e2e.js';
import { syncOnce } from '../sync/engine.js';
import { db, readHouseholdIds } from './local.js';
import type { KraftwerkDatabase } from './local.js';
import { resizeToJpeg } from './photos.js';

export type ImageOutcome = 'done' | 'offline' | 'tooLarge' | 'quotaFull' | 'locked' | 'failed';

export interface ImageDeps {
  put?: (recipeId: string, blob: Blob) => Promise<Response>;
  prepare?: (file: Blob) => Promise<Blob>;
  /** Einmal abgleichen — für ein Gericht, das der Server noch nicht kennt. */
  sync?: () => Promise<unknown>;
}

const defaultPut = (recipeId: string, blob: Blob) =>
  fetch(`${API_URL}/recipes/${encodeURIComponent(recipeId)}/image`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': blob.type || PHOTO_CONTENT_TYPE },
    body: blob,
  });

const defaultGet = (url: string) => fetch(url, { credentials: 'include' });

/** Adresse des Titelbilds — `imageAt` ist zugleich die Versionsmarke gegen alte Bilder im Cache. */
export function recipeImageUrl(recipe: { id: string; imageAt?: string | null }): string | null {
  if (!recipe.imageAt) return null;
  return `${API_URL}/recipes/${recipe.id}/image?v=${encodeURIComponent(recipe.imageAt)}`;
}

async function sealWith(blob: Blob, key: CryptoKey): Promise<Blob> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return new Blob([(await sealFile(bytes, key)) as Uint8Array<ArrayBuffer>], {
    type: SEALED_FILE_CONTENT_TYPE,
  });
}

/** Mit dem Kontoschlüssel zuerst, sonst mit jedem, den das Gerät kennt. */
async function openAny(
  bytes: Uint8Array<ArrayBuffer>,
  database: KraftwerkDatabase,
): Promise<{ plain: Uint8Array; own: boolean } | null> {
  const account = await readAccountKey(database);
  for (const key of await knownKeys(database)) {
    try {
      return { plain: await openFile(bytes, key), own: key === account };
    } catch {
      // Nächster.
    }
  }
  return null;
}

export async function uploadRecipeImage(
  recipeId: string,
  file: Blob,
  deps: ImageDeps = {},
  database: KraftwerkDatabase = db,
): Promise<ImageOutcome> {
  const put = deps.put ?? defaultPut;
  let blob: Blob;
  try {
    // Verkleinert im Browser (1600 px, JPEG, ohne EXIF) — wie Tagebuchfotos.
    blob = deps.prepare ? await deps.prepare(file) : (await resizeToJpeg(file)).blob;
  } catch {
    return 'tooLarge';
  }
  // Verschlüsselt das Konto, geht nie ein lesbares Bild hinaus.
  const key = await readAccountKey(database);
  if (key) blob = await sealWith(blob, key);
  else if (await readEncryptionSetup(database)) return 'locked';

  try {
    let response = await put(recipeId, blob);
    // 404: Das Gericht ist eben erst entstanden und noch nicht beim Server.
    // Einmal abgleichen und noch einmal versuchen.
    if (response.status === 404) {
      await (deps.sync ?? (() => syncOnce()))();
      response = await put(recipeId, blob);
    }
    if (response.status === 413) {
      const body = (await response.json().catch(() => ({}))) as { reason?: string };
      return body.reason === 'quota' ? 'quotaFull' : 'tooLarge';
    }
    if (response.status === 409) return 'locked';
    if (!response.ok) return 'failed';
    const { imageAt } = (await response.json()) as { imageAt: string };
    // Nur lokal vermerken, nicht über die Outbox: `imageAt` setzt der Server,
    // der nächste Abgleich bringt denselben Wert.
    await database.recipes.update(recipeId, { imageAt });
    return 'done';
  } catch {
    return 'offline';
  }
}

export async function removeRecipeImage(
  recipeId: string,
  database: KraftwerkDatabase = db,
): Promise<ImageOutcome> {
  try {
    const response = await fetch(`${API_URL}/recipes/${encodeURIComponent(recipeId)}/image`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) return 'failed';
    await database.recipes.update(recipeId, { imageAt: null });
    return 'done';
  } catch {
    return 'offline';
  }
}

/**
 * Das Titelbild als anzeigbarer Blob — verschlüsselte öffnet das Gerät
 * selbst. `locked`: verschlüsselt, und kein Schlüssel passt.
 */
export async function loadRecipeImage(
  recipe: { id: string; imageAt?: string | null },
  database: KraftwerkDatabase = db,
  get: (url: string) => Promise<Response> = defaultGet,
): Promise<Blob | 'locked' | null> {
  const url = recipeImageUrl(recipe);
  if (!url) return null;
  const response = await get(url).catch(() => null);
  if (!response?.ok) return null;
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!looksSealed(bytes)) return new Blob([bytes], { type: PHOTO_CONTENT_TYPE });
  const opened = await openAny(bytes, database);
  if (!opened) return 'locked';
  return new Blob([opened.plain as Uint8Array<ArrayBuffer>], { type: PHOTO_CONTENT_TYPE });
}

const CHECKED_META = 'e2eRecipeImagesChecked';

async function readChecked(database: KraftwerkDatabase): Promise<Set<string>> {
  const value = (await database.meta.get(CHECKED_META))?.value;
  try {
    return new Set(value ? (JSON.parse(value) as string[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * Rezeptbilder mit dem eigenen Kontoschlüssel verschlüsseln (E21): lesbar
 * Hochgeladenes von vor dem Einrichten und geteilte Kopien, die mit dem
 * Haushaltsschlüssel kamen. Jede Fassung (`imageAt`) wird einmal geprüft;
 * scheitert eine, bleibt sie, wie sie ist, und kommt beim nächsten Mal dran.
 */
export async function resealRecipeImages(
  database: KraftwerkDatabase = db,
  get: (url: string) => Promise<Response> = defaultGet,
  put: (recipeId: string, blob: Blob) => Promise<Response> = defaultPut,
): Promise<{ sealed: number; left: number }> {
  const key = await readAccountKey(database);
  if (!key) return { sealed: 0, left: 0 };
  const checked = await readChecked(database);
  const recipes = await database.recipes
    .filter((recipe) => Boolean(recipe.imageAt) && !recipe.deletedAt)
    .toArray();
  let sealed = 0;
  let left = 0;
  // Fassungen, die es nach diesem Lauf gibt — nur die bleiben in der Liste.
  const current = new Set(recipes.map((recipe) => `${recipe.id}@${recipe.imageAt}`));
  for (const recipe of recipes) {
    const mark = `${recipe.id}@${recipe.imageAt}`;
    if (checked.has(mark)) continue;
    const response = await get(recipeImageUrl(recipe)!).catch(() => null);
    if (!response?.ok) {
      left += 1;
      continue;
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    let plain: Uint8Array | null = bytes;
    if (looksSealed(bytes)) {
      const opened = await openAny(bytes, database);
      // Schon mit dem eigenen Schlüssel — oder mit keinem bekannten: nichts zu tun.
      plain = opened && !opened.own ? opened.plain : null;
    }
    if (!plain) {
      checked.add(mark);
      continue;
    }
    const blob = await sealWith(new Blob([plain as Uint8Array<ArrayBuffer>]), key);
    const answer = await put(recipe.id, blob).catch(() => null);
    if (!answer?.ok) {
      left += 1;
      continue;
    }
    const { imageAt } = (await answer.json()) as { imageAt: string };
    await database.recipes.update(recipe.id, { imageAt });
    checked.add(`${recipe.id}@${imageAt}`);
    current.add(`${recipe.id}@${imageAt}`);
    sealed += 1;
  }
  // Nur Fassungen, die es noch gibt — sonst wächst die Liste ohne Ende.
  await database.meta.put({
    key: CHECKED_META,
    value: JSON.stringify([...checked].filter((mark) => current.has(mark))),
  });
  return { sealed, left };
}

/**
 * Fürs Teilen (E21): ein verschlüsseltes Bild mit dem Haushaltsschlüssel neu
 * packen, base64 für `POST /recipes/:id/share`. `undefined`: lesbar, der
 * Server kopiert es selbst — oder es lässt sich für den Haushalt nicht packen,
 * dann bekommt der andere das Gericht ohne Bild.
 */
export async function imageForHousehold(
  recipe: { id: string; imageAt?: string | null },
  database: KraftwerkDatabase = db,
  get: (url: string) => Promise<Response> = defaultGet,
): Promise<string | undefined> {
  const url = recipeImageUrl(recipe);
  if (!url) return undefined;
  const householdId = (await readHouseholdIds(database))?.[0];
  const householdKey = householdId ? await readHouseholdKey(householdId, database) : null;
  if (!householdKey) return undefined;
  const response = await get(url).catch(() => null);
  if (!response?.ok) return undefined;
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (!looksSealed(bytes)) return undefined;
  const opened = await openAny(bytes, database);
  if (!opened) return undefined;
  const resealed = await sealFile(opened.plain as Uint8Array<ArrayBuffer>, householdKey);
  let binary = '';
  for (const byte of resealed) binary += String.fromCharCode(byte);
  return btoa(binary);
}
